
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, X, Edit3, Trash2, Eye, Lock, PencilRuler } from 'lucide-react';
import { EstimateStratigraphy } from '@/hooks/useEstimateStratigraphies';
import { useEditStratigraphyForEstimate } from '@/hooks/useEditStratigraphyForEstimate';
import { toast } from 'sonner';

interface EstimateStratigraphyActionsProps {
  item: EstimateStratigraphy & { stratigraphy?: any };
  editingId: string | null;
  canEdit: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  /** Se la row è orfana (originale eliminata) chiama questo invece di navigare al configuratore. */
  onViewSnapshot?: () => void;
}

const EstimateStratigraphyActions = ({
  item,
  editingId,
  canEdit,
  isUpdating,
  isDeleting,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onViewSnapshot,
}: EstimateStratigraphyActionsProps) => {
  const navigate = useNavigate();
  // Row orfana: snapshot conservato ma originale del catalogo eliminata.
  // Il configuratore non può aprirla (non ha id valido in `stratigraphies`),
  // quindi mostriamo un viewer dedicato che legge dallo snapshot.
  const isOrphan = !!item.isSnapshot && !item.originalStratigraphyId;

  const { prepareEdit, isPreparing } = useEditStratigraphyForEstimate();

  /**
   * L'occhio VISUALIZZA e basta.
   *
   * Prima navigava al configuratore passando `viewOnly=true`, parametro che
   * nessuno legge piu' da quando il V1 e' stato dismesso: apriva quindi un
   * editor sulla parete di CATALOGO. Il viewer in sola lettura esisteva gia'
   * (era riservato alle righe orfane), qui vale per tutte.
   */
  const handleView = () => {
    onViewSnapshot?.();
  };

  /**
   * Modifica la parete PER QUESTO PREVENTIVO.
   *
   * Se la parete e' condivisa, certificata o usata da altri preventivi, ne
   * viene creata una copia dell'organizzazione e la riga ci viene agganciata.
   * Solo dopo si apre il configuratore, sulla copia.
   */
  const handleEditWall = async () => {
    const targetId = item.stratigraphyId || item.originalStratigraphyId;
    if (!targetId) {
      onViewSnapshot?.();
      return;
    }
    try {
      const esito = await prepareEdit({
        estimateStratigraphyId: item.id,
        estimateId: item.estimateId,
        stratigraphyId: targetId,
      });

      if (esito.copiaCreata) {
        toast.info(
          esito.altriPreventivi > 0
            ? `Creata una copia per questo preventivo: la parete e' usata in altri ${esito.altriPreventivi} preventivi e non viene toccata.`
            : "Creata una copia per questo preventivo: la parete di catalogo non viene toccata.",
        );
      }

      const tipo =
        item.stratigraphy?.type ??
        (typeof item.stratigraphyData === 'object' && item.stratigraphyData
          ? (item.stratigraphyData as { type?: string }).type
          : undefined);

      // I controsoffitti hanno il proprio builder, che usa il param `id`.
      const params = new URLSearchParams(
        tipo === 'ceiling'
          ? { id: esito.stratigraphyId, estimate: item.estimateId, fromEstimateRow: item.id }
          : { stratigraphyId: esito.stratigraphyId, estimate: item.estimateId, fromEstimateRow: item.id },
      );
      navigate(`${tipo === 'ceiling' ? '/ceilings/configurator' : '/configurator'}?${params.toString()}`);
    } catch {
      // il toast d'errore lo emette gia' l'hook
    }
  };

  if (editingId === item.id) {
    return (
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={onSaveEdit}
          disabled={isUpdating}
          className="h-8 w-8 p-0"
        >
          <Check className="h-4 w-4 text-green-600" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancelEdit}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4 text-gray-600" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      <Button
        size="sm"
        variant="ghost"
        onClick={handleView}
        className="h-8 w-8 p-0"
        title="Visualizza composizione (sola lettura)"
      >
        <Eye className="h-4 w-4 text-blue-600" />
      </Button>
      {!isOrphan && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleEditWall}
          disabled={!canEdit || isPreparing}
          className={`h-8 w-8 p-0 ${!canEdit ? 'cursor-not-allowed' : ''}`}
          title={canEdit
            ? "Modifica la parete per questo preventivo (crea una copia, il catalogo non viene toccato)"
            : "Non modificabile: preventivo contrattualizzato"}
        >
          {canEdit ? (
            <PencilRuler className="h-4 w-4 text-indigo-600" />
          ) : (
            <Lock className="h-4 w-4 text-gray-400" />
          )}
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={onStartEdit}
        disabled={!canEdit}
        className={`h-8 w-8 p-0 ${!canEdit ? 'cursor-not-allowed' : ''}`}
        title={canEdit ? "Modifica nome e superficie della riga" : "Non modificabile: preventivo contrattualizzato"}
      >
        {canEdit ? (
          <Edit3 className="h-4 w-4 text-blue-600" />
        ) : (
          <Lock className="h-4 w-4 text-gray-400" />
        )}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onDelete}
        disabled={isDeleting || !canEdit}
        className={`h-8 w-8 p-0 ${canEdit ? 'text-red-600 hover:text-red-700' : 'text-gray-400 cursor-not-allowed'}`}
        title={canEdit ? "Elimina stratigrafia" : "Non eliminabile: preventivo contrattualizzato"}
      >
        {canEdit ? (
          <Trash2 className="h-4 w-4" />
        ) : (
          <Lock className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
};

export default EstimateStratigraphyActions;
